import { setDefaultsForCustomColumnDataTypes } from '@components/providers/LineItemsProvider/utils/setDefaultsForCustomColumnDataTypes';
import { getColumnHeaderDataTypes, getCustomColumnTypes } from 'types';
import type { CustomColumnHeader, ID, LineItem, Lot, LotChanges } from 'types';

interface FillPendingColumnCellsParams {
  changes: Record<ID, LotChanges>;
  lot: Lot;
  rows: LineItem[];
}

const hasPendingCell = (changes: Record<ID, LotChanges>, lotId: ID, row: LineItem, headerId: ID): boolean => (
  Object.values(changes[lotId]?.line_items?.[row.id]?.custom_column_items ?? {})
    .some(cell => String(cell?.custom_column_header_id) === String(headerId))
);

/**
 * Fills the rows of a page that arrives after a column add, rename, visibility or type change with the cells
 * each pending descriptive column needs (blank, or converted for a type change, as
 * `setDefaultsForCustomColumnDataTypes` writes them), skipping any cell that already has a pending entry for
 * that column, so a value the buyer typed is never blanked.
 */
export const fillPendingColumnCells = ({ changes, lot, rows }: FillPendingColumnCellsParams): Record<ID, LotChanges> => {
  const pendingHeaders = Object.values(changes[lot.id]?.custom_column_headers ?? {})
    .filter((header): header is CustomColumnHeader => Boolean(header) && !header._destroy);
  if (!pendingHeaders.length || !rows.length) return changes;

  return pendingHeaders.reduce((nextChanges, header) => {
    const saved = lot.custom_column_headers.find(item => String(item.id) === String(header.id));
    const columnType = header.column_type ?? saved?.column_type;
    if (columnType !== getCustomColumnTypes().DESCRIPTIVE) return nextChanges;

    const rowsToWalk = rows.filter(row => !hasPendingCell(nextChanges, lot.id, row, header.id));
    if (!rowsToWalk.length) return nextChanges;

    const dataType = header.data_type ?? saved?.data_type ?? getColumnHeaderDataTypes().TEXT;

    return setDefaultsForCustomColumnDataTypes({
      changes: nextChanges,
      dataType,
      headerId: header.id,
      isDataTypeChanged: !saved || dataType !== saved.data_type,
      lotId: lot.id,
      lots: [{ ...lot, line_items: rowsToWalk }],
    });
  }, changes);
};
